import clsx from "clsx";
import { useState } from "react";
import { rateFor } from "../../shared/money";
import { ErrorNotice, Notice, Spinner } from "../../web/components/ui";
import { dateTime, timeAgo } from "../../web/lib/format";
import { useMoney } from "../../web/lib/money";
import { can, useHistory, usePriceListSaver } from "./data";
import { type ChangeRow, type PriceListData, REVIEW_ACTIONS, type TrackedObject } from "./meta";

/*
 * History (history.ts) in an item's, supplier's or bundle's window: its History tab (every change,
 * here or in ServiceM8), and at the top of the window, its changes from ServiceM8 no one has seen
 * yet, with Seen. ServiceM8 doesn't say who made a change there, so those say "In ServiceM8".
 */

/** What each tracked field is called. */
const LABELS: Record<TrackedObject, Record<string, string>> = {
    material: {
        name: "Name",
        item_number: "Item number",
        price: "Price",
        cost: "Cost",
        price_includes_taxes: "Prices include tax",
        tax_rate_uuid: "Tax rate",
        barcode: "Barcode",
        item_description: "Description",
        item_is_inventoried: "Tracks stock",
        use_description_for_invoicing: "Description on invoices",
        quantity_in_stock: "In stock",
        active: "Archived",
    },
    supplier: { name: "Name", email: "Email", phone: "Phone", address: "Address", business_number: "Business number", account_number: "Account number", active: "Archived" },
    materialbundle: { name: "Name", item_number: "Item number", material_list: "Items", active: "Archived" },
};

type Formatter = ReturnType<typeof useMoney>;

/** A bundle's items, by name ("2 × Red remote, Blue remote"). */
function bundleLines(value: unknown, data: PriceListData) {
    if (!Array.isArray(value) || !value.length) return "none";
    const names = new Map(data.materials.map((m) => [m.uuid, m.name]));
    return (value as { uuid: string; quantity: number }[]).map((l) => `${l.quantity === 1 ? "" : `${l.quantity} × `}${names.get(l.uuid) ?? "an item"}`).join(", ");
}

/** One value as people read it: money, a tax rate's name, yes or no, a bundle's items, text in quotes, or "blank". */
function shown(field: string, value: unknown, data: PriceListData, fmt: Formatter): string {
    if (value === null || value === undefined || value === "") return "blank";
    if (field === "price" || field === "cost") return fmt.money(value as string);
    if (field === "quantity_in_stock") return fmt.number(value as number);
    if (field === "tax_rate_uuid") return rateFor(String(value), data.taxRates)?.name ?? "a tax rate no longer there";
    if (field === "material_list") return bundleLines(value, data);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return `"${String(value)}"`;
}

/** A change in a few words: "Price: $90.00 → $100.00", "Archived", "Restored", "Created here". */
function changeText(object: TrackedObject, c: ChangeRow, data: PriceListData, fmt: Formatter) {
    if (c.field === "created") return "Created";
    if (c.field === "active") return c.after ? "Restored" : "Archived";
    const label = LABELS[object][c.field] ?? c.field;
    return `${label}: ${shown(c.field, c.before, data, fmt)} → ${shown(c.field, c.after, data, fmt)}`;
}

/** Where it was made: "In ServiceM8", or here ("By Matt", or "Here" when who isn't known). */
const madeWhere = (c: ChangeRow) => {
    if (c.source === "servicem8") return "In ServiceM8";
    return c.by ? `By ${c.by}` : "Here";
};

/**
 * Each field's overall change, newest first: where it started (before the earliest) and where it
 * is now (after the latest), however many times it changed. One changed and then changed back is
 * left out (nothing differs). Every step is still in the History tab.
 */
function overall(changes: readonly ChangeRow[]): ChangeRow[] {
    // Newest first: the first seen of a field is its latest; the last, its earliest.
    const byField = new Map<string, { latest: ChangeRow; earliest: ChangeRow }>();
    for (const c of changes) {
        const seen = byField.get(c.field);
        if (seen) seen.earliest = c;
        else byField.set(c.field, { latest: c, earliest: c });
    }
    return [...byField.values()]
        .map(({ latest, earliest }) => ({ ...latest, before: earliest.before }))
        .filter((c) => c.field === "created" || JSON.stringify(c.before) !== JSON.stringify(c.after));
}

/** A field's name, as the change says it ("Price"; "Archived" for whether it's archived). */
const fieldName = (object: TrackedObject, field: string) => LABELS[object][field] ?? field;

/** What changed, in a title's words: "price", or "archived"/"restored" for whether it's archived. */
const changedField = (object: TrackedObject, c: ChangeRow) => {
    if (c.field === "active") return c.after ? "restored" : "archived";
    return fieldName(object, c.field).toLowerCase();
};

/** "price, cost and name". */
function listed(words: readonly string[]) {
    if (words.length <= 1) return words.join("");
    return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

/** At the top of a window: its changes from ServiceM8 no one has seen yet (each field's overall change), and Seen. Nothing when there are none. */
export function ChangedNotice({ object, uuid, data }: Readonly<{ object: TrackedObject; uuid: string; data: PriceListData }>) {
    if (!data.changedInServiceM8[object].includes(uuid)) return null;
    return <ChangedNoticeShown object={object} uuid={uuid} data={data} />;
}

function ChangedNoticeShown({ object, uuid, data }: Readonly<{ object: TrackedObject; uuid: string; data: PriceListData }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    // The detail lines open on request, so the box stays short (a line per issue).
    const [open, setOpen] = useState(false);
    const history = useHistory(object, uuid);
    const changes = overall((history.data ?? []).filter((c) => !c.seen));
    // Seen marks only these (a change arriving after they loaded stays to be seen).
    const unseen = (history.data ?? []).filter((c) => !c.seen).map((c) => c.id);
    const more = changes.length > 3 ? changes.length - 3 : 0;
    const fields = changes.map((c) => changedField(object, c));
    // Until its changes are here, only that it changed: not "nothing differs", and no Seen without seeing them.
    if (!history.data) {
        return (
            <>
                <Notice tone="warning" title="Changed in ServiceM8.">
                    {history.error ? "Its changes couldn't be loaded: see the History tab." : "Loading what changed..."}
                </Notice>
                <ErrorNotice error={history.error} />
            </>
        );
    }
    return (
        <>
            <Notice
                tone="warning"
                title={fields.length ? `Changed in ServiceM8: ${listed(fields)}.` : "Changed in ServiceM8, then changed back:"}
                actions={
                    can(data, REVIEW_ACTIONS[object]) ? (
                        <button type="button" className="btn-secondary" onClick={() => saver.markSeen(object, [uuid], unseen).catch((err: Error) => setError(err))}>
                            Seen
                        </button>
                    ) : null
                }
            >
                {fields.length ? (
                    <>
                        Please check {fields.length === 1 ? "it's" : "they're"} right.{" "}
                        <button type="button" className="cursor-pointer text-xs underline underline-offset-2 hover:no-underline" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
                            {open ? "Hide changes" : "Show changes"}
                        </button>
                    </>
                ) : (
                    // Every field changed was changed back: nothing to check, only Seen.
                    <>nothing differs now.</>
                )}
                {open
                    ? changes.slice(0, 3).map((c) => {
                          const text = changeText(object, c, data, fmt);
                          // One line each: a long value (a name) is cut short; the whole of it shows on hover.
                          return (
                              <span key={c.id} className="mt-0.5 block truncate text-xs" title={`${text} (${dateTime(c.at)})`}>
                                  {text} <span className="opacity-80">({timeAgo(c.at)})</span>
                              </span>
                          );
                      })
                    : null}
                {open && more ? <span className="mt-0.5 block text-xs opacity-80">and {more.toLocaleString()} more (History tab)</span> : null}
            </Notice>
            <ErrorNotice error={error} />
        </>
    );
}

/** A window's History tab: every change, newest first; ones from ServiceM8 not seen yet are flagged. */
export function HistoryTab({ object, uuid, data }: Readonly<{ object: TrackedObject; uuid: string; data: PriceListData }>) {
    const fmt = useMoney();
    const history = useHistory(object, uuid);
    if (history.isLoading) return <Spinner />;
    if (!history.data) return <ErrorNotice error={history.error ?? new Error("The history couldn't be loaded.")} />;
    if (!history.data.length) {
        return <p className="p-4 text-sm text-muted">No changes yet. Changes made here or in ServiceM8 from now on show here.</p>;
    }
    return (
        <table className="list is-fixed">
            <colgroup>
                <col className="w-[22%]" />
                <col />
                <col className="w-[22%]" />
            </colgroup>
            <thead>
                <tr>
                    <th>When</th>
                    <th>Change</th>
                    <th>Where</th>
                </tr>
            </thead>
            <tbody>
                {history.data.map((c) => (
                    <tr key={c.id} className={clsx(!c.seen && "is-warning")}>
                        <td className="text-muted" title={dateTime(c.at)}>
                            {timeAgo(c.at)}
                        </td>
                        <td className="break-words">{changeText(object, c, data, fmt)}</td>
                        <td className="text-muted">{madeWhere(c)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
